import {
  EmailNotification,
  EmailNotificationData,
  EmailNotificationTypes,
} from '../../shared/types/emailNotification';

// Static party labels (ASCII only). Centralised so copy stays consistent and
// the exact fallback strings live in one place.
export const SYSTEM_SENDER = 'Coright (system)';
export const NO_RECIPIENT = '(recipient not stored)';
export const CORIGHT_SUPPORT_INBOX = '(Coright support inbox)';
export const CORIGHT_ACCESS_INBOX = '(Coright access inbox)';
export const UNKNOWN_PARTY = '(unknown)';
export const EMPTY_PARTY = '(not set)';

export type PartyCtx = { email: string; data: EmailNotificationData };
export type PartyResolver = (ctx: PartyCtx) => string;
export type PartiesEntry = { from: PartyResolver; to: PartyResolver };

// Returns the value at `key` only when it is a non-empty string, else undefined.
function str(data: EmailNotificationData, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

// Shared recipient resolver: the row's `email` column, or a fallback if empty.
const toEmail: PartyResolver = (c) => c.email || EMPTY_PARTY;

// Strategy A — system -> recipient.
const systemToRecipient: PartiesEntry = {
  from: () => SYSTEM_SENDER,
  to: toEmail,
};

// Strategy B — sender in blob.
const senderInBlob: PartiesEntry = {
  from: (c) => str(c.data, 'Sender_Email') ?? str(c.data, 'Sender_Name') ?? SYSTEM_SENDER,
  to: toEmail,
};

// Strategy C — inviter in blob.
const inviterInBlob: PartiesEntry = {
  from: (c) => str(c.data, 'Inviter_Email') ?? str(c.data, 'Inviter_Name') ?? SYSTEM_SENDER,
  to: toEmail,
};

// Strategy D — done-editing mixed blob shapes.
const doneEditing: PartiesEntry = {
  from: (c) => str(c.data, 'Sender_Email') ?? str(c.data, 'Applied_By_Name') ?? SYSTEM_SENDER,
  to: toEmail,
};

// Strategy E — invitation family (email column is the sender/inviter).
const invitationFamily: PartiesEntry = {
  from: (c) => c.email || UNKNOWN_PARTY,
  to: (c) => str(c.data, 'Recipient_Email') ?? NO_RECIPIENT,
};

// Strategy F — inbound to a Coright inbox (email column is the requester).
const inboundSupport: PartiesEntry = {
  from: (c) => c.email || UNKNOWN_PARTY,
  to: () => CORIGHT_SUPPORT_INBOX,
};

const inboundAccess: PartiesEntry = {
  from: (c) => c.email || UNKNOWN_PARTY,
  to: () => CORIGHT_ACCESS_INBOX,
};

// Default — used for types with no emitter (e.g. ONE_DAY_REMINDER) and, at
// runtime, for any `type` value not present in the map.
const defaultStrategy: PartiesEntry = {
  from: () => UNKNOWN_PARTY,
  to: toEmail,
};

// Directly-annotated total object literal: tsc errors if any
// EmailNotificationTypes member is unmapped (missing-key) and if a key is not a
// valid enum member (excess-property). This compile-time coverage replaces a
// runtime test (the repo has no FE test runner).
//
// NOTE: the repo-pinned typescript@4.1.x cannot parse a transitive
// @remix-run/router .d.ts; those are parse (not semantic) errors, so they
// survive `skipLibCheck` and abort plain `npx tsc --noEmit` before this literal
// is ever checked. To actually surface a missing-key error here, compile this
// file in isolation:
//   npx tsc --noEmit --skipLibCheck --strict src/modules/emailNotifications/notificationPartiesMap.ts
export const notificationPartiesMap: Record<EmailNotificationTypes, PartiesEntry> = {
  [EmailNotificationTypes.DELIVERY_APPROVED]: systemToRecipient,
  [EmailNotificationTypes.DELIVERY_APPROVED_VIEWER]: systemToRecipient,
  [EmailNotificationTypes.CHANGED_PASSWORD]: systemToRecipient,
  [EmailNotificationTypes.FORGOT_PASSWORD]: systemToRecipient,
  [EmailNotificationTypes.USAGE_ABOUT_TO_EXPIRE]: systemToRecipient,
  [EmailNotificationTypes.USAGE_EXPIRED]: systemToRecipient,
  [EmailNotificationTypes.SIGNATURE_REJECTED]: systemToRecipient,
  [EmailNotificationTypes.SIGNUP_CONFIRMATION]: systemToRecipient,

  [EmailNotificationTypes.WITHDRAWN]: senderInBlob,
  [EmailNotificationTypes.DELIVERY_APPROVAL_REQUEST]: senderInBlob,
  [EmailNotificationTypes.DELIVERY_VIEW_REQUEST]: senderInBlob,
  [EmailNotificationTypes.NON_USER_ACCESS]: senderInBlob,
  [EmailNotificationTypes.THREE_DAYS_REMINDER]: senderInBlob,
  [EmailNotificationTypes.IGNORED_REMINDER]: senderInBlob,
  [EmailNotificationTypes.CORIGHT_CLOUD_SHARED_MEDIA]: senderInBlob,

  [EmailNotificationTypes.SIGNATURE_REQUEST_EXPIRED]: inviterInBlob,

  [EmailNotificationTypes.DONE_EDITING_CONTRACT]: doneEditing,

  [EmailNotificationTypes.PROFILE_ACCESS_INVITATION]: invitationFamily,
  [EmailNotificationTypes.COMPANY_ACCESS_INVITATION]: invitationFamily,
  [EmailNotificationTypes.PROJECT_COMPANY_ACCESS_INVITATION]: invitationFamily,
  [EmailNotificationTypes.COMPANY_INVITATION]: invitationFamily,
  [EmailNotificationTypes.PROJECT_COMPANY_ACCESS]: invitationFamily,

  [EmailNotificationTypes.CONTACT_US]: inboundSupport,
  [EmailNotificationTypes.REQUEST_ACCESS]: inboundAccess,

  [EmailNotificationTypes.ONE_DAY_REMINDER]: defaultStrategy,
};

// Defensive parse of the stringified `data` blob. Tolerates null/undefined/
// non-string and malformed JSON, always returning a plain object.
export function parseData(raw: unknown): EmailNotificationData {
  if (typeof raw !== 'string' || raw === '') return {};
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as EmailNotificationData) : {};
  } catch {
    return {};
  }
}

export function resolveParties(n: EmailNotification): { from: string; to: string } {
  const ctx: PartyCtx = { email: n.email ?? '', data: parseData(n.data) };
  const strategy = notificationPartiesMap[n.type] ?? defaultStrategy;
  return {
    from: strategy.from(ctx) || EMPTY_PARTY,
    to: strategy.to(ctx) || EMPTY_PARTY,
  };
}
